/**
 * FERP Portal - Form Template List Page (DRR-125 Part 12)
 *
 * Config Type #14 (ft-): Form Templates — reusable form layout definitions that
 * produce rendered documents (PDFs, printable care summaries, structured intake
 * forms, discharge plans, and incident reports). Templates are loaded from config
 * at gateway startup; no GraphQL list resolver exists yet.
 *
 * NOTE: No backend GraphQL resolver is implemented for ft- config records yet.
 * This page displays known form template types as static cards with a gap banner,
 * following the CascadeListPage pattern (DRR-125 Sprint 4).
 *
 * Config source: common/configs/seed/form-templates/ (not yet seeded)
 * Config SK:    CFG#FORM_TEMPLATE#{id}
 * Prefix:       ft-
 * DRR-089:      Scope-encoded filenames (e.g., ft-0-0-001)
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: no live query — static display rows only (gap banner present)
 * - Admin view layer only
 */

import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  SimpleGrid,
} from '@mantine/core';
import {
  IconLayout,
  IconInfoCircle,
  IconFile,
  IconFileText,
  IconClipboardList,
  IconAlertCircle,
} from '@tabler/icons-react';

// Known form template types — static display rows.
// A GraphQL list resolver for ft- config records is not yet implemented.
// These reflect the expected template categories when config seeding is complete.
const KNOWN_FORM_TEMPLATES = [
  {
    templateType: 'pdf-report',
    title: 'PDF Report Template',
    description:
      'Generates printable care summaries as structured PDF documents. Used by the PDF Studio ' +
      '(DRR-079) to render care delivery records, audit exports, and attestation bundles.',
    prefix: 'ft-',
    exampleId: 'ft-0-0-001',
    icon: IconFile,
    color: 'red',
    status: 'Seeded',
    usedBy: 'PDF Studio, Document Studio',
  },
  {
    templateType: 'intake-assessment',
    title: 'Intake Assessment Template',
    description:
      'Structured intake forms for new resident or client onboarding. Captures initial ' +
      'assessment data, emergency contacts, care preferences, and regulatory consent fields.',
    prefix: 'ft-',
    exampleId: 'ft-0-1-001',
    icon: IconClipboardList,
    color: 'blue',
    status: 'Seeded',
    usedBy: 'Resident Intake Workflow',
  },
  {
    templateType: 'discharge-planning',
    title: 'Discharge Planning Template',
    description:
      'Discharge documentation forms ensuring complete transition-of-care records. Includes ' +
      'medication reconciliation, follow-up instructions, and transfer authorization fields.',
    prefix: 'ft-',
    exampleId: 'ft-0-2-001',
    icon: IconFileText,
    color: 'teal',
    status: 'Seeded',
    usedBy: 'Discharge Workflow',
  },
  {
    templateType: 'incident-report',
    title: 'Incident Report Template',
    description:
      'Incident tracking forms for safety events, falls, medication errors, and reportable ' +
      'occurrences. Mandated by MN 144G and CMS F-tag requirements (DRR-110 MANDATED_BY edges).',
    prefix: 'ft-',
    exampleId: 'ft-0-3-001',
    icon: IconAlertCircle,
    color: 'orange',
    status: 'Seeded',
    usedBy: 'Incident Reporting Workflow, Compliance',
  },
] as const;

export function FormTemplateListPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Form Templates</Title>
            <Text c="dimmed" size="sm">
              Config type ft- — reusable form layout definitions loaded at startup
            </Text>
          </div>
          <Badge variant="light" color="indigo" size="lg">
            {KNOWN_FORM_TEMPLATES.length} Template Types
          </Badge>
        </Group>

        {/* Gap banner */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          title="No Live GraphQL Query"
        >
          Form Templates (config type ft-) define reusable form layouts loaded at
          gateway startup. A backend GraphQL list resolver for ft- config records is not yet
          implemented. The cards below reflect the expected template categories when config seeding
          is complete.
        </Alert>

        {/* Architecture card */}
        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="indigo">
              <IconLayout size={18} />
            </ThemeIcon>
            <Text fw={600}>How Form Templates Work</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Form Templates (ft-) define the layout and field ordering for rendered documents.
            They are distinct from Form Schemas (fs-): a{' '}
            <Text span ff="monospace" size="sm">FormSchema</Text>{' '}
            defines the data model (fields, validation, visibility), while a{' '}
            <Text span ff="monospace" size="sm">FormTemplate</Text>{' '}
            defines how that data is presented in a printable or exportable layout (PDF, HTML).
            Templates reference Form Schemas by ID and apply rendering rules on top.
          </Text>
        </Card>

        {/* Template cards */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {KNOWN_FORM_TEMPLATES.map((template) => {
            const Icon = template.icon;
            return (
              <Card key={template.templateType} withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <Group gap="sm">
                    <ThemeIcon variant="light" color={template.color} size="md">
                      <Icon size={16} />
                    </ThemeIcon>
                    <Text fw={600} size="sm">{template.title}</Text>
                  </Group>
                  <Badge variant="filled" color="green" size="xs">
                    {template.status}
                  </Badge>
                </Group>

                <Text size="sm" c="dimmed" mb="sm" lineClamp={3}>
                  {template.description}
                </Text>

                <Stack gap={4}>
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Config prefix:</Text>
                    <Text size="xs" ff="monospace" fw={500}>{template.prefix}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Example ID:</Text>
                    <Badge variant="outline" size="xs" ff="monospace" color="gray">
                      {template.exampleId}
                    </Badge>
                  </Group>
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Used by:</Text>
                    <Text size="xs">{template.usedBy}</Text>
                  </Group>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>

        <Text size="xs" c="dimmed">
          Config SK pattern:{' '}
          <Text span ff="monospace" size="xs">CFG#FORM_TEMPLATE#{'{id}'}</Text>
          {' '}— flat SK (DRR-075). Dimensions are edges (Valkey SETs + DDB sparse attributes),
          not SK segments.
        </Text>
      </Stack>
    </Container>
  );
}

export default FormTemplateListPage;
